import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Building2,
  Users,
  Activity,
  MapPin,
  Calendar,
  ChevronLeft,
  ChevronRight,
  FileText,
  TrendingUp,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatDate, formatDayMonth } from '../lib/dates';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getWeekStartFromOffset = (weekOffset = 0) => {
  const currentDate = new Date();
  const dayOfTheWeek = currentDate.getDay();
  const diff = currentDate.getDate() - (dayOfTheWeek + 1);

  const weekStart = new Date(currentDate);
  weekStart.setDate(diff + (weekOffset * 7));

  return weekStart;
};

const toDateInputString = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const formatWeekRange = (startOfTheWeek) => {
  if (!startOfTheWeek) return '';

  const start = new Date(startOfTheWeek);

  const end = new Date(start);
  end.setDate(end.getDate() + 7);

  const options = { day: 'numeric', month: 'short' };

  return `${start.toLocaleDateString('es-MX', options)} - ${end.toLocaleDateString('es-MX', options)}, ${start.getFullYear()}`;
};

const getBeneficiaries = (activity = {}) =>
  Number(
    activity.total_beneficiaries ??
    activity.total_beneficiaries ??
    activity.total_beneficiaries ??
    0
  ) || 0;

const getActions = (activity = {}) =>
  Number(
    activity.acciones_realizadas ??
    activity.total_acciones ??
    activity.acciones ??
    0
  ) || 0;

const getVisits = (activity = {}) =>
  Number(
    activity.total_visits ??
    0
  ) || 0;

const getTerritoryName = (activity = {}) =>
  activity.territory_name || activity.territorio_name || 'Territorio no disponible';

const getActivityDateLabel = (activity = {}) =>
  formatDate(
    activity.week_start || activity.start_date || activity.created_at?.slice(0, 10)
  ) || '—';

export const DependencyDetail = () => {
  const { id } = useParams();

  const [dependency, setDependency] = useState(null);
  const [activities, setActivities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [currentWeek, setCurrentWeek] = useState('');
  const [startOfTheWeek, setStartOfTheWeek] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const weekStart = getWeekStartFromOffset(weekOffset);
        const weekStartStr = toDateInputString(weekStart);

        setCurrentWeek(weekStartStr);
        setStartOfTheWeek(weekStart);

        const [depRes, activitiesRes, summaryRes] = await Promise.all([
          axios.get(`${API}/dependencies/${id}`),
          axios.get(`${API}/activities?dependency_id=${id}&week_start=${weekStartStr}&status=published`),
          axios.get(`${API}/summaries/dependency/${id}?week_start=${weekStartStr}`),
        ]);

        setDependency(depRes.data || null);
        setActivities(Array.isArray(activitiesRes.data) ? activitiesRes.data : []);
        setSummary(summaryRes.data || null);
      } catch (error) {
        console.error('Error cargando detalle de dependencia:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, weekOffset]);

  const trendData = useMemo(() => {
    const weeklyData = activities.reduce((acc, act) => {
      const week = act.week_start || act.start_date || 'unknown';

      if (!acc[week]) {
        acc[week] = {
          week,
          beneficiarios: 0,
          acciones: 0,
          actividades: 0,
        };
      }

      acc[week].beneficiarios += getBeneficiaries(act);
      acc[week].acciones += getActions(act);
      acc[week].actividades += 1;

      return acc;
    }, {});

    return Object.values(weeklyData)
      .sort((a, b) => a.week.localeCompare(b.week))
      .slice(-8);
  }, [activities]);

  const territoryChartData = useMemo(() => {
    if (!summary?.by_territory) return [];

    return summary.by_territory.map((item) => {
      const fullName = item.territory?.name || 'Territorio no disponible';
      const shortName =
        fullName.length > 12 ? `${fullName.substring(0, 12)}...` : fullName;

      return {
        name: shortName,
        fullName,
        beneficiarios: Number(item.beneficiarios || 0),
        actividades: Array.isArray(item.activities) ? item.activities.length : 0,
      };
    });
  }, [summary]);

  const weeklyBeneficiarios = useMemo(
    () =>
      Number(
        summary?.summary?.total_beneficiarios ??
        summary?.summary?.total_beneficiados ??
        0
      ) || 0,
    [summary]
  );

  const weeklyAcciones = useMemo(
    () =>
      Number(
        summary?.summary?.total_acciones ??
        summary?.summary?.acciones ??
        0
      ) || 0,
    [summary]
  );

  const totalActivitiesLoaded = activities.length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F0E1]">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-[#8B1C4A]/20" />
          <p className="text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!dependency) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F0E1]">
        <div className="text-center">
          <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Dependencia no encontrada</p>
          <Link to="/dependencias">
            <Button className="mt-4">Volver a Dependencias</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F0E1]" data-testid="dependency-detail">
      <section
        className="text-white py-12"
        style={{ backgroundColor: dependency.color || '#8B1C4A' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/dependencias"
            className="inline-flex items-center gap-1 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Volver a Dependencias
          </Link>

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
            <div>
              <Badge className="bg-white/20 text-white mb-2">
                {dependency.acronym || '—'}
              </Badge>
              <h1 className="text-2xl lg:text-3xl font-bold tracking-tight max-w-2xl">
                {dependency.name}
              </h1>
            </div>

            <div className="flex gap-6">
              <div className="text-center">
                <p className="text-3xl font-bold">{weeklyBeneficiarios.toLocaleString()}</p>
                <p className="text-white/80 text-sm">Beneficiarios</p>
              </div>

              <div className="text-center">
                <p className="text-3xl font-bold">{totalActivitiesLoaded}</p>
                <p className="text-white/80 text-sm">Actividades</p>
              </div>

              <div className="text-center">
                <p className="text-3xl font-bold">{weeklyAcciones.toLocaleString()}</p>
                <p className="text-white/80 text-sm">Acciones</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 bg-white rounded-lg p-2 border border-[#D4C5A5]/30 w-fit">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setWeekOffset((prev) => prev - 1)}
              className="hover:bg-[#F5F0E1]"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>

            <div className="flex items-center gap-2 px-4">
              <Calendar className="w-5 h-5 text-[#8B1C4A]" />
              <span className="font-medium text-slate-700">
                {formatWeekRange(startOfTheWeek)}
              </span>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setWeekOffset((prev) => prev + 1)}
              disabled={weekOffset >= 0}
              className="hover:bg-[#F5F0E1]"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      <section className="py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="bg-white border border-[#D4C5A5]/30">
              <TabsTrigger value="overview">Resumen</TabsTrigger>
              <TabsTrigger value="activities">Actividades</TabsTrigger>
              <TabsTrigger value="territories">Por Territorio</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <div className="grid lg:grid-cols-2 gap-6">
                <Card className="border border-[#D4C5A5]/30">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp
                        className="w-5 h-5"
                        style={{ color: dependency.color || '#8B1C4A' }}
                      />
                      Tendencia Semanal
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    <div className="h-[250px]">
                      {trendData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={trendData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#D4C5A5" />
                            <XAxis
                              dataKey="week"
                              tick={{ fontSize: 11 }}
                              tickFormatter={(value) => formatDayMonth(value)}
                            />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip labelFormatter={(label) => formatDate(label)} />
                            <Legend />
                            <Line
                              type="monotone"
                              dataKey="beneficiarios"
                              stroke={dependency.color || '#8B1C4A'}
                              strokeWidth={2}
                              name="Beneficiarios"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-full flex items-center justify-center text-slate-500">
                          Sin datos
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border border-[#D4C5A5]/30">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-[#235B4E]" />
                      Distribución por Territorio
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    <div className="h-[250px]">
                      {territoryChartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={territoryChartData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#D4C5A5" />
                            <XAxis type="number" />
                            <YAxis
                              dataKey="name"
                              type="category"
                              width={90}
                              tick={{ fontSize: 10 }}
                            />
                            <Tooltip
                              labelFormatter={(label, payload) =>
                                payload?.[0]?.payload?.fullName || label
                              }
                            />
                            <Bar
                              dataKey="beneficiarios"
                              fill={dependency.color || '#8B1C4A'}
                              radius={[0, 4, 4, 0]}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-full flex items-center justify-center text-slate-500">
                          Sin datos
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {summary && (
                <Card className="border border-[#D4C5A5]/30">
                  <CardHeader>
                    <CardTitle>
                      Resumen Semanal - {formatDate(summary.week_start) || 'Semana actual'}
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    <div className="grid sm:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <p
                          className="text-2xl font-bold"
                          style={{ color: dependency.color || '#8B1C4A' }}
                        >
                          {summary.summary?.total_activities || 0}
                        </p>
                        <p className="text-sm text-slate-500">Actividades</p>
                      </div>

                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold text-[#8B1C4A]">
                          {weeklyBeneficiarios.toLocaleString()}
                        </p>
                        <p className="text-sm text-slate-500">Beneficiarios</p>
                      </div>

                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold text-[#BC955C]">
                          {weeklyAcciones.toLocaleString()}
                        </p>
                        <p className="text-sm text-slate-500">Visitas</p>
                      </div>

                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold text-[#235B4E]">
                          {summary.summary?.territories_covered || 0}
                        </p>
                        <p className="text-sm text-slate-500">Territorios</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="activities">
              <Card className="border border-[#D4C5A5]/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#BC955C]" />
                    Todas las Actividades ({activities.length})
                  </CardTitle>
                </CardHeader>

                <CardContent>
                  {activities.length > 0 ? (
                    <div className="space-y-4">
                      {activities.map((activity) => {
                        const territoryName = getTerritoryName(activity);
                        const beneficiaries = getBeneficiaries(activity);
                        const activityDate = getActivityDateLabel(activity);
                        const visits = getVisits(activity);

                        return (
                          <Link
                            key={activity.id}
                            to={`/actividad/${activity.id}`}
                            className="block"
                          >
                            <div className="p-4 rounded-lg border border-slate-200 hover:border-[#8B1C4A]/50 hover:shadow-sm transition-all">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <h3 className="font-semibold text-slate-800">
                                    {activity.title || 'Actividad sin título'}
                                  </h3>

                                  <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {territoryName}
                                  </p>

                                  <div className="flex items-center gap-4 mt-2 text-sm">
                                    <span className="flex items-center gap-1 text-[#8B1C4A]">
                                      <Users className="w-4 h-4" />
                                      {beneficiaries.toLocaleString()}
                                    </span>

                                    <span className="flex items-center gap-1 text-[#BC955C]">
                                      <Activity className="w-4 h-4" />
                                      {visits}
                                    </span>

                                    <span className="flex items-center gap-1 text-slate-500">
                                      <Calendar className="w-4 h-4" />
                                      {activityDate}
                                    </span>
                                  </div>
                                </div>

                                {activity.photos?.length > 0 && (
                                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-100 shrink-0">
                                    <img
                                      src={
                                        activity.photos[0].startsWith('/api')
                                          ? `${process.env.REACT_APP_BACKEND_URL}${activity.photos[0]}`
                                          : activity.photos[0]
                                      }
                                      alt=""
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Activity className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No hay actividades registradas</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="territories">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {summary?.by_territory?.map((item, index) => (
                  <Link key={index} to={`/territorio/${item.territory?.id}`}>
                    <Card className="border border-[#D4C5A5]/30 hover:shadow-md transition-all">
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-slate-800">
                          {item.territory?.name || 'Territorio no disponible'}
                        </h3>
                        <p className="text-sm text-slate-500">
                          {item.territory?.alcaldia || '—'}
                        </p>

                        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100">
                          <div>
                            <p
                              className="text-lg font-bold"
                              style={{ color: dependency.color || '#8B1C4A' }}
                            >
                              {(item.beneficiarios || 0).toLocaleString()}
                            </p>
                            <p className="text-xs text-slate-500">Beneficiarios</p>
                          </div>

                          <div>
                            <p className="text-lg font-bold text-slate-700">
                              {item.activities?.length || 0}
                            </p>
                            <p className="text-xs text-slate-500">Actividades</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}

                {(!summary?.by_territory || summary.by_territory.length === 0) && (
                  <div className="col-span-full text-center py-8">
                    <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">
                      Sin actividades en territorios esta semana
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default DependencyDetail;